import React, { createElement, memo, useEffect, useState } from "react";
import CountUp from "react-countup";
import PortableText from "@sanity/block-content-to-react";
import styled from "styled-components";
import { isBrowser, formatTitle, removeSpacesAndCommasFromArrayOfId } from "@util/helper";
import { Maybe, SanityBlockContent, SanitySlug } from "@graphql-types";
import { P, HeadingStyle } from "@util/standard";
import { Link } from "@global";

const PortableTextStyled = styled.div<{
  inverse?: boolean;
}>`
  ul {
    padding-left: 30px;
  }
  a {
    &:hover {
      opacity: 0.5;
    }
  }

  img {
    height: auto;
    width: auto;
    max-width: 100%;
    margin: 25px 0;
  }

  .block-image {
    max-width: 100%;
    height: 2em;
    margin: 0 10px;
  }

  // To handle images from the block content and display them inline with the text

  /* Target previous sibling of .block-image */
  *:has(+ .block-image) {
    display: inline-block;
  }

  /* Target next sibling of .block-image */
  .block-image + * {
    display: inline-block;
  }
`;

const BlockContentRowStyle = styled.div`
  margin-bottom: 15px;
  &:last-child {
    margin: 0;
  }
`;

interface Props {
  data: Maybe<SanityBlockContent> | undefined;
  slug: Maybe<SanitySlug>;
}

const BlocksContent = ({ data, slug }: Props) => {
  const [pathName, setPathName] = useState("");
  useEffect(() => {
    if (location.pathname) {
      setPathName(location.pathname);
    }
  }, [isBrowser() && location.pathname]);

  if (data == null) {
    return null;
  }

  const { _rawBlockContent } = data;

  if (_rawBlockContent == null) {
    return null;
  }
  const NumberBox = ({ code }: { code: number }) => {
    const formattingFn = (value: any) => {
      if (value < 100) return `0${value}`;
      else return value;
    };

    return (
      <span className="product-code">
        <span>
          <CountUp start={1000} end={code} duration={2} formattingFn={formattingFn} />
        </span>
      </span>
    );
  };
  const block = (props: any) => {
    const { style = "normal" } = props.node;
    const tag = style;

    if (pathName.includes(`brands/x-shot/lock-blaster/code`)) {
      if (props.children[0] === "") return null;

      const code = String(props.children[0]).split(": ").pop();

      if (code) {
        return (
          <>
            <div className="number-box">
              <span className="code">Code</span>
              <NumberBox code={Number(code)} />
            </div>
          </>
        );
      }
    } else {
      if (/^h\d/.test(tag)) {
        //As in pravicy-policy page There table of contents which includes title and onClick of title we have to scroll/move down to appropriate section/description so here we have added dynamic id based on text of title in the title.
        let resultData = "";
        if (pathName.includes("privacy-policy")) {
          const titleData = props.children[0]?.props?.node?.children;
          resultData = formatTitle(titleData);
        }

        return createElement(tag, { id: resultData, style: { margin: "25px 0" } }, props.children);
      }

      return (
        <BlockContentRowStyle>
          <P>{props.children}</P>
        </BlockContentRowStyle>
      );
    }
  };

  const linkWord = (props: any) => {
    const {
      mark: { url },
      children,
    } = props;
    if (url == null || children == null) return null;
    const { internalLink, externalLink } = url;
    const attributes = {
      internalLink,
      externalLink,
      linkText: children,
    };
    return <Link {...attributes} />;
  };

  const heading = (props: any) => {
    return (
      <BlockContentRowStyle>
        <HeadingStyle design={props.node.design} as={props.node.tag}>
          {props.node.text}
        </HeadingStyle>
      </BlockContentRowStyle>
    );
  };

  const button = (props: any) => {
    const productRecallPathName = pathName.includes("voluntary-recall-notice");
    const { node } = props;
    const { buttonDesign } = node;

    const attributes = {
      ...node,
      design: buttonDesign,
      useIcon: "iconRightArrow",
      slug,
    };
    return (
      <BlockContentRowStyle
        className={productRecallPathName ? " product-recall-btn" : "block-content-row-button"}
      >
        <Link {...attributes} />
      </BlockContentRowStyle>
    );
  };

  const image = (props: any) => {
    return <img className="block-image" src={props.node.asset.url} alt={props.node.alt} />;
  };

  const list = (props: any) => {
    const listNumberDataType = props.type === "number";
    let listItem = [];

    if (listNumberDataType) {
      listItem = props.children.map(item => item.props.children);
    }
    return (
      <>
        {listNumberDataType ? (
          listItem.map(item => (
            <div className="block-list">
              <ul className="privacy-title-disc">
                <li>
                  <a href={removeSpacesAndCommasFromArrayOfId(item)}>{item[0]}</a>
                </li>
              </ul>
            </div>
          ))
        ) : (
          <BlockContentRowStyle>
            <ul>{props.children}</ul>
          </BlockContentRowStyle>
        )}
      </>
    );
  };

  return (
    <PortableTextStyled>
      <PortableText
        blocks={_rawBlockContent}
        serializers={{
          types: { block, heading, button, image, divider: () => <hr /> },
          marks: { linkWord },
          list: list,
          // listItem: listItem,
        }}
        imageOptions={{ auto: "format", fit: "fill" }}
        projectId={process.env.SANITY_PROJECT_ID}
        dataset={process.env.GATSBY_NODE_ENV ?? "development"}
      />
    </PortableTextStyled>
  );
};

export default memo(BlocksContent);
